<template>
  <div class="network-diagram-content-container scroll-fire-fox" id="scroll">
    <div ref="networkDiagram" style="height: fit-content">
      <network-diagram-node-and-cables
        v-if="!diagramIsLoading && hasChildren"
        :node-type="diagramData.tl_technical_site_logical_type"
        :node-data="diagramData"
        :hasPreviousSibling="false"
        :hasNextSibling="false"
        :style="{ zoom: zoom + '%' }"
      />
    </div>

    <loading :active.sync="loading" :is-full-page="true" color="#4fa2b7" />

    <div
      v-if="!diagramIsLoading && !startingPointSelected"
      class="warning-text"
    >
      <div class="message-title">
        {{ $t("NoStartingPointSelectedMessage") }}
      </div>

      <div class="message-description">
        {{ $t("NoStartingPointSelectedHint") }}
      </div>
    </div>

    <div
      v-if="!diagramIsLoading && startingPointWithoutDiagramSelected"
      class="warning-text"
    >
      <div class="message-title">{{ $t("NoNetworkDiagramFound") }}</div>

      <div class="message-description">
        {{ $t("NoNetworkDiagramFoundMessage") }}
      </div>
    </div>

    <div class="zoom-buttons-container">
      <v-btn
        fab
        color="primary"
        :disabled="diagramAbsent"
        @click="zoomIn"
        class="ma-1"
        height="40"
        width="40"
      >
        <v-icon size="25">zoom_in</v-icon>
      </v-btn>

      <v-btn
        fab
        color="primary"
        :disabled="diagramAbsent"
        @click="zoomOut"
        class="ma-1"
        height="40"
        width="40"
      >
        <v-icon size="25">zoom_out</v-icon>
      </v-btn>

      <v-btn
        fab
        color="primary"
        :disabled="diagramAbsent"
        @click="refresh"
        class="ma-1"
        height="40"
        width="40"
      >
        <v-icon size="25">refresh</v-icon>
      </v-btn>

      <v-btn
        fab
        color="primary"
        :disabled="diagramAbsent"
        @click="exportToPDF"
        class="ma-1"
        height="40"
        width="40"
      >
        <v-icon>picture_as_pdf</v-icon>
      </v-btn>

      <v-btn
        fab
        color="primary"
        :disabled="diagramAbsent"
        @click="exportToCAD"
        class="ma-1"
        height="40"
        width="40"
      >
        <v-icon>mdi-file-cad</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mapActions } from "vuex";
import NetworkDiagramNode from "./NetworkDiagramNode";
import NetworkDiagramNodeAndCables from "./NetworkDiagramNodeAndCables";
import RefreshSVG from "@/assets/home/tabs/network/refresh.svg";
import ZoomInSVG from "@/assets/map_icons/zoom_in.svg";
import ZoomOutSVG from "@/assets/map_icons/zoom_out.svg";

import Loading from "vue-loading-overlay";
import html2pdf from "html2pdf.js";
import { bus } from "@/main";

export default {
  name: "NetworkDiagramDiagram",

  components: {
    NetworkDiagramNodeAndCables,
    NetworkDiagramNode,
    RefreshSVG,
    ZoomInSVG,
    ZoomOutSVG,
    Loading
  },

  data() {
    return {
      /** Zoom value of the diagram component in percentage. */
      zoom: 100,
      exportIsLoading: false,
      diagramHeight: null,
      diagramWidth: null
    };
  },

  computed: {
    ...mapGetters({
      startingPointSelected: "isNetworkDiagramStartingPointSelected",
      networkStartingPointUUID: "getNetworkDiagramTechnicalLocalUUID",
      diagramData: "getNetworkDiagramData",
      diagramIsLoading: "isNetworkDiagramLoading"
    }),

    /**
     * Checks if the currently selected Technical Local has children.
     * @returns {boolean}
     */
    hasChildren() {
      if (this.diagramData) {
        let children = this.diagramData.children;
        return (
          children.length > 0 ||
          (children.constructor === Object &&
            Object.entries(children).length !== 0)
        );
      } else return false;
    },

    /**
     * Checks if there is a Starting point previously selected and it has no children.
     * @returns {boolean}
     */
    startingPointWithoutDiagramSelected() {
      return this.startingPointSelected && !this.hasChildren;
    },

    /**
     * Checks if there is data to show a network diagram.
     * @returns {boolean}
     */
    diagramAbsent() {
      return (
        !this.startingPointSelected || this.startingPointWithoutDiagramSelected
      );
    },

    workspaceUUID() {
      let workspaceUUID = this.$store.getters.getNetworkDiagramWorkspaceUUID;
      if (!workspaceUUID) {
        workspaceUUID = this.$store.getters.workspace.uuid;
      }
      return workspaceUUID;
    },

    loading() {
      return this.diagramIsLoading || this.exportIsLoading;
    },

    scrollPositions() {
      return this.$store.getters.getScrollPosition;
    }
  },

  watch: {
    diagramData(updatedData) {
      if (updatedData) {
        this.$nextTick(() => {
          this.diagramHeight = this.$refs.networkDiagram.scrollHeight;
          this.diagramWidth = this.$refs.networkDiagram.scrollWidth;
        });
      }
    }
  },

  created() {
    bus.$on("updateActiveTab", newValue => {
      if (newValue.name === "network") {
        this.$nextTick(() => {
          document
            .getElementById("scroll")
            .scrollTo(this.scrollPositions.x, this.scrollPositions.y);
        });
      } else {
        if (document.getElementById("scroll")) {
          this.setScrollPosition({
            x: document.getElementById("scroll").scrollLeft,
            y: document.getElementById("scroll").scrollTop
          });
        }
      }
    });
  },

  destroyed() {
    /*bus.$off("updateActiveTab");*/
  },

  methods: {
    ...mapActions([
      "GET_WORKSPACE_TECHNICAL_SITES_WITH_LOCALS",
      "GET_NETWORK_DIAGRAM_DATA",
      "getNetworkDiagramAsCADFile",
      "setScrollPosition"
    ]),

    /**
     * Updates the list of Technical Sites with Technical Locals and the network diagram data, by requesting them from the
     * backend.
     */
    refresh() {
      this.GET_WORKSPACE_TECHNICAL_SITES_WITH_LOCALS(this.workspaceUUID);

      this.GET_NETWORK_DIAGRAM_DATA({
        nodeUUID: this.networkStartingPointUUID,
        workspaceUUID: this.workspaceUUID
      });
    },

    /**
     * Increases the zoom on the diagram.
     */
    zoomIn() {
      if (this.zoom > 10) this.zoom += 10;
      if (this.zoom >= 1 && this.zoom <= 10) this.zoom += 1;
    },

    /**
     * Decreases the zoom on the diagram.
     */
    zoomOut() {
      if (this.zoom > 10) this.zoom -= 10;
      if (this.zoom > 1 && this.zoom <= 10) this.zoom -= 1;
    },

    async exportToPDF() {
      let zoom = this.zoom;
      try {
        let orientation = "l";
        if (this.diagramHeight > this.diagramWidth) {
          orientation = "p";
        }

        this.zoom = 100;

        let opt = {
          margin: 1,
          filename: "network_diagram " + new Date().toLocaleString() + ".pdf",
          html2canvas: {
            onclone: element => {
              const svgElements = Array.from(element.querySelectorAll("svg"));
              svgElements.forEach(s => {
                const bBox = s.getBBox();
                let width = bBox.width;
                let height = bBox.height;
                if (width > 50) width = 50;
                if (height > 50) height = 50;
                s.setAttribute("x", bBox.x);
                s.setAttribute("y", bBox.y);
                s.setAttribute("width", width);
                s.setAttribute("height", height);
              });
            }
          },
          jsPDF: {
            orientation: orientation,
            unit: "mm",
            format: [this.diagramWidth, this.diagramHeight]
          }
        };

        this.exportIsLoading = true;
        await html2pdf(this.$refs.networkDiagram, opt);
      } catch (error) {
        console.log(error);
      } finally {
        this.zoom = zoom;
        this.exportIsLoading = false;
      }
    },

    exportToCAD() {
      let nodes = [];
      let cables = [];

      document.querySelectorAll(".network-diagram-node").forEach(el => {
        let node = {
          top: el.offsetTop,
          left: el.offsetLeft,
          uuid: el.dataset.nodeUuid,
          endpoint: el.dataset.nodeEndpoint
        };
        nodes.push(node);
      });
      document.querySelectorAll(".network-diagram-cable").forEach(el => {
        cables.push(el.dataset.objectUuid);
      });

      this.exportIsLoading = true;
      this.getNetworkDiagramAsCADFile({
        nodes: nodes,
        cables: cables
      })
        .then(data => {
          window.open(data.data.file_url_public);
        })
        .catch(error => {
          console.log(error);
        })
        .then(() => {
          this.exportIsLoading = false;
        });
    }
  }
};
</script>

<style scoped>
.network-diagram-content-container {
  width: 100%;
  height: 100%;
  padding: 30px 50px 50px 100px;
  display: flex;
}

.zoom-buttons-container {
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 20px;
}

#scroll {
  overflow: auto;
}
</style>

<style>
.network-diagram-hover-container {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  background-color: transparent;
  box-shadow: 0 0 3px 0 rgba(138, 138, 138, 0.4);
  cursor: pointer;
  transform: translateZ(20px);
}
.network-diagram-hover-button {
  width: 21px;
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/Network/NetworkDiagram/NetworkDiagram.vue